// Pure, non-interactive row primitive for one audit_events row.
//
// Shared between:
//   - AuditEventList (web UI, wrapped with a useState disclosure
//     toggle for the payload)
//   - the audit PDF document (server-renderable, payload always
//     rendered, styled by pdf-styles.ts via semantic class names)
//
// .ts (not .tsx) so the file is reachable from Vercel's function
// tracer cleanly — every .tsx file under the audit-PDF function's
// transitive import graph has caused trace gaps in this PR, so the
// convention is now "no .tsx in any module that pdf.ts reaches."
// JSX is replaced with React.createElement; the rendered DOM is
// byte-identical to the JSX form. AuditEventList.tsx (which lives
// in the web app's Vite bundle) imports this primitive normally —
// `.ts` vs `.tsx` is transparent to Vite + tsc, only the source
// authoring style differs.
//
// Every styled element carries BOTH the existing Tailwind utility
// classes (which keep the on-screen audit view byte-identical to its
// pre-extract appearance) AND a semantic class name like `audit-row`
// (which the PDF stylesheet targets). Tailwind utilities are no-ops
// in the PDF (no Tailwind output loaded in the Vercel function),
// and the semantic classes coexist with the utilities harmlessly on
// the web.

import { createElement, type ReactNode } from 'react';

export type AuditEventRow = {
  id: string;
  occurred_at: string;
  event_type: string;
  actor_user_id: string | null;
  actor_type: string;
  payload: unknown;
  // Joined from public.users via audit_events_actor_user_id_fkey.
  // NULL when the actor is a system / cron / webhook write.
  actor_user: { full_name: string | null; email: string } | null;
  // Optional entity context. Per-entity timelines (order detail,
  // driver detail) already render in the entity's own page so they
  // don't pass these; the tenant-wide audit browse view DOES pass
  // them so the flat list stays legible.
  entity_type?: string | null;
  entity_id?: string | null;
};

export function formatAuditTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function auditActorDisplay(row: AuditEventRow): string {
  if (row.actor_user?.full_name) return row.actor_user.full_name;
  if (row.actor_user?.email) return row.actor_user.email;
  if (row.actor_user_id) return row.actor_user_id;
  return row.actor_type;
}

export function auditEntityCaption(row: AuditEventRow): string | null {
  if (row.entity_type && row.entity_id) {
    return `${row.entity_type} · ${row.entity_id.slice(0, 8)}`;
  }
  return row.entity_type ?? null;
}

export function auditPayloadIsRenderable(row: AuditEventRow): boolean {
  return row.payload !== null && row.payload !== undefined && row.payload !== '';
}

type Props = {
  row: AuditEventRow;
  heading: string;
  // Render the payload inline (true) or omit it (false). The web list
  // wraps this with a disclosure toggle; the PDF passes payloadOpen=true
  // since a printed page has no "click to expand" affordance.
  payloadOpen: boolean;
  // Optional trailing slot (the web list's "Show payload" button lives
  // here; the PDF passes nothing).
  trailing?: ReactNode;
};

// One row of an audit timeline. Same DOM shape as the prior JSX
// version; createElement only because this module must stay .ts
// for Vercel function tracing.
export function AuditEventRowView({
  row,
  heading,
  payloadOpen,
  trailing,
}: Props): JSX.Element {
  const hasPayload = auditPayloadIsRenderable(row);
  const entityCaption = auditEntityCaption(row);

  const headingP = createElement(
    'p',
    { className: 'audit-heading font-medium text-ink' },
    heading,
  );

  const metaP = createElement(
    'p',
    { className: 'audit-meta text-xs text-ink-2' },
    `${formatAuditTimestamp(row.occurred_at)} · ${auditActorDisplay(row)}`,
  );

  const entityP = entityCaption
    ? createElement(
        'p',
        { className: 'audit-meta text-xs text-ink-2' },
        `Entity: ${entityCaption}`,
      )
    : null;

  const headText = createElement(
    'div',
    { className: 'audit-row-head-text' },
    headingP,
    metaP,
    entityP,
  );

  const head = createElement(
    'div',
    { className: 'audit-row-head flex items-baseline justify-between gap-3' },
    headText,
    trailing ?? null,
  );

  const payload =
    hasPayload && payloadOpen
      ? createElement(
          'pre',
          {
            className:
              'audit-payload mt-2 overflow-x-auto rounded bg-bg px-3 py-2 text-xs text-ink',
          },
          JSON.stringify(row.payload, null, 2),
        )
      : null;

  return createElement(
    'li',
    {
      className:
        'audit-row rounded-md border border-border bg-bg-soft px-3 py-2',
    },
    head,
    payload,
  );
}
